import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Archive, ArrowLeft,
} from 'lucide-react';
import { projectsApi, driversApi, workItemsApi, authApi } from '../api/services';
import type {
  Project, ProjectStatus, RAGStatus, ProjectMember, ProjectMemberRole,
  ProjectOutcome, OutcomeStatus, ProjectIndicator, IndicatorType, IndicatorDirection,
  AnnualStrategicDriver, WorkItem,
} from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';
import DriverPill from '../components/DriverPill';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useEntitlements } from '../context/EntitlementsContext';
import { usePermissions } from '../context/PermissionsContext';

const ragValues: RAGStatus[] = ['Green', 'Amber', 'Red'];
const statusValues: ProjectStatus[] = ['Planning', 'Active', 'OnHold', 'Completed', 'Cancelled'];
const outcomeStatuses: OutcomeStatus[] = ['NotStarted', 'InProgress', 'Achieved', 'Missed', 'Deferred'];
const memberRoles: ProjectMemberRole[] = ['Owner', 'Sponsor', 'Contributor', 'Informed'];

// Radix Select can't accept '' as a value; sentinel stands in for "no driver".
const NONE = '__none__';

const RAG_TONE: Record<RAGStatus, string> = {
  Green: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  Amber: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  Red: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

const STATUS_TONE: Record<ProjectStatus, string> = {
  Planning: 'border-sky-500 text-sky-600 dark:text-sky-400',
  Active: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
  OnHold: 'border-amber-500 text-amber-600 dark:text-amber-400',
  Completed: 'border-border text-muted-foreground',
  Cancelled: 'border-rose-500 text-rose-600 dark:text-rose-400',
};

const OUTCOME_TONE: Record<OutcomeStatus, string> = {
  NotStarted: 'bg-muted text-muted-foreground',
  InProgress: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  Achieved: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  Missed: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  Deferred: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
};

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { toast } = useToast();
  // The driver link shows only on plans with strategic drivers; a project
  // keeps its link either way.
  const driversIncluded = useEntitlements().has('strategicDrivers');
  // Every project write needs Manage Projects; an admin can take it away per
  // user, and then the project reads as it is.
  const canManage = usePermissions().can('canManageProjects');

  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [outcomes, setOutcomes] = useState<ProjectOutcome[]>([]);
  const [indicators, setIndicators] = useState<ProjectIndicator[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [report, setReport] = useState<any>(null);
  const [users, setUsers] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const [tabValue, setTabValue] = useState('overview');

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState<Partial<Project>>({});

  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<ProjectMemberRole>('Contributor');

  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [newOutcome, setNewOutcome] = useState<Partial<ProjectOutcome>>({ title: '', description: '', targetDate: '' });

  const [indicatorDialogOpen, setIndicatorDialogOpen] = useState(false);
  const [newIndicator, setNewIndicator] = useState<Partial<ProjectIndicator>>({
    title: '', description: '', indicatorType: 'Leading', unit: '', targetValue: undefined, direction: 'HigherIsBetter',
  });

  const [recordValueDialogOpen, setRecordValueDialogOpen] = useState(false);
  const [recordIndicatorId, setRecordIndicatorId] = useState('');
  const [recordValue, setRecordValue] = useState<number | string>('');
  const [recordNotes, setRecordNotes] = useState('');

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projectData, membersData, outcomesData, indicatorsData, driversData] = await Promise.all([
        projectsApi.getById(projectId),
        projectsApi.getMembers(projectId),
        projectsApi.getOutcomes(projectId),
        projectsApi.getIndicators(projectId),
        driversIncluded
          ? driversApi.getActive().catch(() => [] as AnnualStrategicDriver[])
          : Promise.resolve([] as AnnualStrategicDriver[]),
      ]);
      setProject(projectData);
      setMembers(membersData);
      setOutcomes(outcomesData);
      setIndicators(indicatorsData);
      setDrivers(driversData);

      try {
        const allItems = await workItemsApi.getAll();
        setWorkItems(allItems.filter(wi => wi.projectId === projectId));
      } catch {
        setWorkItems([]);
      }

      try {
        setReport(await projectsApi.getReport(projectId));
      } catch {
        setReport(null);
      }

      try {
        const usersData = await authApi.getUsers();
        setUsers(usersData.map((u: any) => ({ id: u.id, displayName: u.displayName, email: u.email })));
      } catch {
        setUsers([]);
      }
    } catch (err) {
      // silent-by-design: the empty `Project not found` state below
      // already communicates the failure to the user.
      console.error('Failed to load project', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, driversIncluded]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRagCycle = async () => {
    if (!project) return;
    const idx = ragValues.indexOf(project.ragStatus);
    const next = ragValues[(idx + 1) % ragValues.length];
    try {
      await projectsApi.update(project.id, { ragStatus: next });
      setProject(prev => prev ? { ...prev, ragStatus: next } : prev);
    } catch (err: any) {
      console.error('Failed to update RAG status', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update RAG status' });
    }
  };

  const handleStatusCycle = async () => {
    if (!project) return;
    const idx = statusValues.indexOf(project.status);
    const next = statusValues[(idx + 1) % statusValues.length];
    try {
      await projectsApi.update(project.id, { status: next });
      setProject(prev => prev ? { ...prev, status: next } : prev);
    } catch (err: any) {
      console.error('Failed to update status', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update status' });
    }
  };

  const handleEditSave = async () => {
    if (!project) return;
    try {
      const updated = await projectsApi.update(project.id, editProject);
      setProject(updated);
      setEditDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to update project', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update project' });
    }
  };

  const handleArchive = async () => {
    if (!project) return;
    const ok = await confirm({
      title: 'Archive this project?',
      body: 'Archived projects are hidden from active lists but can be restored later.',
      confirmLabel: 'Archive',
      destructive: true,
    });
    if (!ok) return;
    try {
      await projectsApi.archive(project.id);
      navigate('/projects');
    } catch (err: any) {
      console.error('Failed to archive project', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to archive project' });
    }
  };

  const handleAddMember = async () => {
    if (!project || !newMemberUserId) return;
    try {
      await projectsApi.addMember(project.id, { userId: newMemberUserId, role: newMemberRole });
      setMemberDialogOpen(false);
      setNewMemberUserId('');
      setNewMemberRole('Contributor');
      setMembers(await projectsApi.getMembers(project.id));
    } catch (err: any) {
      console.error('Failed to add member', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to add member' });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!project) return;
    const ok = await confirm({
      title: 'Remove member?',
      body: 'They will lose access to this project. You can re-add them later.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await projectsApi.removeMember(project.id, memberId);
      setMembers(await projectsApi.getMembers(project.id));
    } catch (err: any) {
      console.error('Failed to remove member', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to remove member' });
    }
  };

  const handleAddOutcome = async () => {
    if (!project || !newOutcome.title) return;
    try {
      await projectsApi.createOutcome(project.id, {
        ...newOutcome,
        targetDate: newOutcome.targetDate || undefined,
      });
      setOutcomeDialogOpen(false);
      setNewOutcome({ title: '', description: '', targetDate: '' });
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err: any) {
      console.error('Failed to add outcome', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to add outcome' });
    }
  };

  const handleOutcomeStatusCycle = async (outcome: ProjectOutcome) => {
    if (!project) return;
    const idx = outcomeStatuses.indexOf(outcome.status);
    const next = outcomeStatuses[(idx + 1) % outcomeStatuses.length];
    try {
      await projectsApi.updateOutcome(project.id, outcome.id, { status: next });
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err: any) {
      console.error('Failed to update outcome status', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update outcome status' });
    }
  };

  const handleDeleteOutcome = async (outcomeId: string) => {
    if (!project) return;
    const ok = await confirm({
      title: 'Delete outcome?',
      body: 'The outcome and its progress history will be permanently removed from this project.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await projectsApi.deleteOutcome(project.id, outcomeId);
      setOutcomes(await projectsApi.getOutcomes(project.id));
    } catch (err: any) {
      console.error('Failed to delete outcome', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete outcome' });
    }
  };

  const handleAddIndicator = async () => {
    if (!project || !newIndicator.title) return;
    try {
      await projectsApi.createIndicator(project.id, newIndicator);
      setIndicatorDialogOpen(false);
      setNewIndicator({ title: '', description: '', indicatorType: 'Leading', unit: '', targetValue: undefined, direction: 'HigherIsBetter' });
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err: any) {
      console.error('Failed to add indicator', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to add indicator' });
    }
  };

  const handleDeleteIndicator = async (indicatorId: string) => {
    if (!project) return;
    const ok = await confirm({
      title: 'Delete indicator?',
      body: 'The indicator and all recorded values will be permanently removed.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await projectsApi.deleteIndicator(project.id, indicatorId);
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err: any) {
      console.error('Failed to delete indicator', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete indicator' });
    }
  };

  const handleRecordValue = async () => {
    if (!project || !recordIndicatorId || recordValue === '') return;
    try {
      await projectsApi.recordValue(project.id, recordIndicatorId, {
        value: Number(recordValue),
        notes: recordNotes || undefined,
      });
      setRecordValueDialogOpen(false);
      setRecordIndicatorId('');
      setRecordValue('');
      setRecordNotes('');
      setIndicators(await projectsApi.getIndicators(project.id));
    } catch (err: any) {
      console.error('Failed to record value', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to record value' });
    }
  };

  const isIndicatorMeetingTarget = (indicator: ProjectIndicator): boolean | null => {
    if (indicator.currentValue == null || indicator.targetValue == null) return null;
    if (indicator.direction === 'HigherIsBetter') return indicator.currentValue >= indicator.targetValue;
    return indicator.currentValue <= indicator.targetValue;
  };

  const leadingIndicators = useMemo(() => indicators.filter(i => i.indicatorType === 'Leading'), [indicators]);
  const laggingIndicators = useMemo(() => indicators.filter(i => i.indicatorType === 'Lagging'), [indicators]);

  const linkedDriver = useMemo(() => {
    if (!project?.strategicDriverId) return null;
    return drivers.find(d => d.id === project.strategicDriverId) ?? null;
  }, [project, drivers]);

  const getDaysRemaining = () => {
    if (!project?.targetEndDate) return null;
    const target = new Date(project.targetEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatMinutes = (mins: number): string => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (!project) {
    return (
      <div>
        <p className="py-6 text-center text-sm text-muted-foreground">
          {loading ? 'Loading project...' : 'Project not found'}
        </p>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();
  const outcomeAchieved = outcomes.filter(o => o.status === 'Achieved').length;

  const ragColor =
    project.ragStatus === 'Red' ? 'bg-rose-500'
    : project.ragStatus === 'Amber' ? 'bg-amber-500'
    : 'bg-emerald-500';

  return (
    <div>
      {/* Header */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="flex-1 text-2xl font-semibold">{project.title}</h2>
        <button
          type="button"
          onClick={handleRagCycle}
          disabled={!canManage}
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            RAG_TONE[project.ragStatus],
          )}
        >
          {project.ragStatus}
        </button>
        <button
          type="button"
          onClick={handleStatusCycle}
          disabled={!canManage}
          className={cn(
            'rounded-full border px-2 py-0.5 text-[11px] font-medium',
            STATUS_TONE[project.status],
          )}
        >
          {project.status}
        </button>
        {canManage && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() => {
                setEditProject({
                  title: project.title,
                  description: project.description,
                  strategicDriverId: project.strategicDriverId,
                  startDate: project.startDate,
                  targetEndDate: project.targetEndDate,
                  progressPercentage: project.progressPercentage,
                  progressMode: project.progressMode,
                  notes: project.notes,
                });
                setEditDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleArchive}
              className="gap-1.5 text-amber-500 hover:bg-amber-500/10"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          </>
        )}
      </div>

      <Tabs value={tabValue} onValueChange={setTabValue}>
        <TabsList className="mb-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="outcomes">Outcomes ({outcomes.length})</TabsTrigger>
          <TabsTrigger value="indicators">Indicators ({indicators.length})</TabsTrigger>
          <TabsTrigger value="workitems">Work Items ({workItems.length})</TabsTrigger>
          <TabsTrigger value="time">Time Invested</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {project.description && (
            <p className="mb-3 text-sm">{project.description}</p>
          )}

          <Card className="mb-3">
            <CardContent className="pt-6">
              <div className="mb-3 flex flex-wrap gap-4">
                {project.startDate && (
                  <div>
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="text-sm font-medium">{project.startDate}</p>
                  </div>
                )}
                {project.targetEndDate && (
                  <div>
                    <p className="text-xs text-muted-foreground">Target End Date</p>
                    <p className="text-sm font-medium">{project.targetEndDate}</p>
                  </div>
                )}
                {daysRemaining !== null && (
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge
                      variant="outline"
                      className={cn(
                        'mt-0.5',
                        daysRemaining < 0 ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                          : daysRemaining <= 7 ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                          : '',
                      )}
                    >
                      {daysRemaining < 0
                        ? `${Math.abs(daysRemaining)} days overdue`
                        : daysRemaining === 0
                          ? 'Due today'
                          : `${daysRemaining} days remaining`}
                    </Badge>
                  </div>
                )}
              </div>

              {driversIncluded && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground">Strategic driver</p>
                  <div className="mt-1">
                    <DriverPill driver={linkedDriver} onClick={() => navigate('/strategy')} />
                  </div>
                </div>
              )}

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Progress</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{project.progressMode}</Badge>
                    <span className="text-xs font-semibold">{project.progressPercentage}%</span>
                  </div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn('h-full rounded-full', ragColor)}
                    style={{ width: `${project.progressPercentage}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-base font-semibold">Members</h3>
                {canManage && (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setMemberDialogOpen(true)}>
                    <Plus className="h-4 w-4" />
                    Add Member
                  </Button>
                )}
              </div>
              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground">No members yet</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {members.map(m => (
                    <div key={m.id} className="flex items-center gap-2">
                      <span className="flex-1 text-sm font-medium">
                        {m.displayName || m.email || m.userId}
                      </span>
                      <Badge variant="outline">{m.role}</Badge>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleRemoveMember(m.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outcomes">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {outcomeAchieved} of {outcomes.length} outcomes achieved
            </p>
            {canManage && (
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setOutcomeDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Outcome
              </Button>
            )}
          </div>

          {outcomes.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground">No outcomes defined yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {outcomes.map(outcome => (
                <Card key={outcome.id}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium">{outcome.title}</p>
                        {outcome.description && (
                          <p className="mt-1 text-sm text-muted-foreground">{outcome.description}</p>
                        )}
                        {outcome.targetDate && (
                          <p className="mt-1 block text-xs text-muted-foreground">Target: {outcome.targetDate}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleOutcomeStatusCycle(outcome)}
                          disabled={!canManage}
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-medium',
                            OUTCOME_TONE[outcome.status],
                          )}
                        >
                          {outcome.status}
                        </button>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDeleteOutcome(outcome.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="indicators">
          {canManage && (
            <div className="mb-3 flex justify-end">
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setIndicatorDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Indicator
              </Button>
            </div>
          )}

          {indicators.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground">No indicators defined yet</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {leadingIndicators.length > 0 && (
                <div className="mb-4">
                  <h3 className="mb-2 text-base font-semibold">Leading Indicators</h3>
                  <IndicatorList
                    indicators={leadingIndicators}
                    isMeetingTarget={isIndicatorMeetingTarget}
                    onRecord={canManage ? (id) => {
                      setRecordIndicatorId(id);
                      setRecordValue('');
                      setRecordNotes('');
                      setRecordValueDialogOpen(true);
                    } : undefined}
                    onDelete={canManage ? handleDeleteIndicator : undefined}
                  />
                </div>
              )}

              {laggingIndicators.length > 0 && (
                <div>
                  <h3 className="mb-2 text-base font-semibold">Lagging Indicators</h3>
                  <IndicatorList
                    indicators={laggingIndicators}
                    isMeetingTarget={isIndicatorMeetingTarget}
                    onRecord={canManage ? (id) => {
                      setRecordIndicatorId(id);
                      setRecordValue('');
                      setRecordNotes('');
                      setRecordValueDialogOpen(true);
                    } : undefined}
                    onDelete={canManage ? handleDeleteIndicator : undefined}
                  />
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="workitems">
          <div className="mb-3 flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() => navigate(`/work-items?projectId=${projectId}`)}
            >
              <Plus className="h-4 w-4" />
              Add Work Item
            </Button>
          </div>

          {workItems.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground">No work items linked to this project</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2">Title</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Priority</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Target Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workItems.map(item => (
                      <tr key={item.id} className="border-b border-border/60 hover:bg-muted/50">
                        <td className="px-3 py-2 font-medium">{item.title}</td>
                        <td className="px-3 py-2"><Badge variant="outline">{item.type}</Badge></td>
                        <td className="px-3 py-2"><PriorityChip priority={item.priority} /></td>
                        <td className="px-3 py-2"><StatusChip status={item.status} /></td>
                        <td className="px-3 py-2">{item.targetDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="time">
          {!report ? (
            <Card>
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground">No time data available yet</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="mb-3">
                <CardContent className="pt-6">
                  <h3 className="mb-1 text-base font-semibold">Total Time Invested</h3>
                  <p className="text-3xl font-bold">
                    {formatMinutes(report.timeInvested?.totalMinutes || 0)}
                  </p>
                  <div className="mt-1 flex gap-3">
                    <p className="text-sm text-muted-foreground">
                      Work Items: {formatMinutes(report.timeInvested?.workItemMinutes || 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Meetings: {formatMinutes(report.timeInvested?.meetingMinutes || 0)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {report.timeInvested?.byWeek && report.timeInvested.byWeek.length > 0 && (
                <Card className="mb-3">
                  <CardContent className="pt-6">
                    <h3 className="mb-2 text-base font-semibold">Weekly Breakdown</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-3 py-2">Week Start</th>
                            <th className="px-3 py-2 text-right">Work Items</th>
                            <th className="px-3 py-2 text-right">Meetings</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.timeInvested.byWeek.map((week: any, i: number) => (
                            <tr key={i} className="border-b border-border/60">
                              <td className="px-3 py-2">{week.weekStart}</td>
                              <td className="px-3 py-2 text-right">{formatMinutes(week.workMinutes || 0)}</td>
                              <td className="px-3 py-2 text-right">{formatMinutes(week.meetingMinutes || 0)}</td>
                              <td className="px-3 py-2 text-right">{formatMinutes((week.workMinutes || 0) + (week.meetingMinutes || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {report.timeInvested?.byMember && report.timeInvested.byMember.length > 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-2 text-base font-semibold">Member Breakdown</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Role</th>
                            <th className="px-3 py-2 text-right">Total Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.timeInvested.byMember.map((member: any, i: number) => (
                            <tr key={i} className="border-b border-border/60">
                              <td className="px-3 py-2">{member.name}</td>
                              <td className="px-3 py-2"><Badge variant="outline">{member.role}</Badge></td>
                              <td className="px-3 py-2 text-right">{formatMinutes(member.totalMinutes || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-title">Title</Label>
              <Input
                id="ep-title"
                value={editProject.title || ''}
                onChange={e => setEditProject(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-desc">Description</Label>
              <Textarea
                id="ep-desc"
                rows={3}
                value={editProject.description || ''}
                onChange={e => setEditProject(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            {driversIncluded && (
              <div className="flex flex-col gap-1.5">
                <Label>Strategic Driver</Label>
                <Select
                  value={editProject.strategicDriverId || NONE}
                  onValueChange={(v) => setEditProject(prev => ({ ...prev, strategicDriverId: v === NONE ? undefined : v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ep-start">Start Date</Label>
                <Input
                  id="ep-start"
                  type="date"
                  value={editProject.startDate || ''}
                  onChange={e => setEditProject(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ep-target">Target End Date</Label>
                <Input
                  id="ep-target"
                  type="date"
                  value={editProject.targetEndDate || ''}
                  onChange={e => setEditProject(prev => ({ ...prev, targetEndDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-progress">Progress (%)</Label>
              <Input
                id="ep-progress"
                type="number"
                min={0}
                max={100}
                value={editProject.progressPercentage ?? 0}
                onChange={e => setEditProject(prev => ({ ...prev, progressPercentage: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Progress Mode</Label>
              <Select
                value={editProject.progressMode || 'Manual'}
                onValueChange={(v) => setEditProject(prev => ({ ...prev, progressMode: v as 'Manual' | 'AutoCalculated' }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual</SelectItem>
                  <SelectItem value="AutoCalculated">Auto Calculated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-notes">Notes</Label>
              <Textarea
                id="ep-notes"
                rows={2}
                value={editProject.notes || ''}
                onChange={e => setEditProject(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>User</Label>
              <Select value={newMemberUserId} onValueChange={setNewMemberUserId}>
                <SelectTrigger><SelectValue placeholder="Select a user" /></SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => !members.some(m => m.userId === u.id))
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.displayName} ({u.email})</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Role</Label>
              <Select value={newMemberRole} onValueChange={(v) => setNewMemberRole(v as ProjectMemberRole)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {memberRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMemberDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMember} disabled={!newMemberUserId}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Outcome Dialog */}
      <Dialog open={outcomeDialogOpen} onOpenChange={setOutcomeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Outcome</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="no-title">Title</Label>
              <Input
                id="no-title"
                value={newOutcome.title || ''}
                onChange={e => setNewOutcome(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="no-desc">Description</Label>
              <Textarea
                id="no-desc"
                rows={2}
                value={newOutcome.description || ''}
                onChange={e => setNewOutcome(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="no-target">Target Date</Label>
              <Input
                id="no-target"
                type="date"
                value={newOutcome.targetDate || ''}
                onChange={e => setNewOutcome(prev => ({ ...prev, targetDate: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddOutcome} disabled={!newOutcome.title}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Indicator Dialog */}
      <Dialog open={indicatorDialogOpen} onOpenChange={setIndicatorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Indicator</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ni-title">Title</Label>
              <Input
                id="ni-title"
                value={newIndicator.title || ''}
                onChange={e => setNewIndicator(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ni-desc">Description</Label>
              <Textarea
                id="ni-desc"
                rows={2}
                value={newIndicator.description || ''}
                onChange={e => setNewIndicator(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label>Type</Label>
                <Select
                  value={newIndicator.indicatorType || 'Leading'}
                  onValueChange={(v) => setNewIndicator(prev => ({ ...prev, indicatorType: v as IndicatorType }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Leading">Leading</SelectItem>
                    <SelectItem value="Lagging">Lagging</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label>Direction</Label>
                <Select
                  value={newIndicator.direction || 'HigherIsBetter'}
                  onValueChange={(v) => setNewIndicator(prev => ({ ...prev, direction: v as IndicatorDirection }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HigherIsBetter">Higher is Better</SelectItem>
                    <SelectItem value="LowerIsBetter">Lower is Better</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ni-unit">Unit</Label>
                <Input
                  id="ni-unit"
                  placeholder="e.g. %, days, count"
                  value={newIndicator.unit || ''}
                  onChange={e => setNewIndicator(prev => ({ ...prev, unit: e.target.value }))}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="ni-target">Target Value</Label>
                <Input
                  id="ni-target"
                  type="number"
                  value={newIndicator.targetValue ?? ''}
                  onChange={e => setNewIndicator(prev => ({ ...prev, targetValue: e.target.value ? parseFloat(e.target.value) : undefined }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIndicatorDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddIndicator} disabled={!newIndicator.title}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Value Dialog */}
      <Dialog open={recordValueDialogOpen} onOpenChange={setRecordValueDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Value</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rv-val">Value</Label>
              <Input
                id="rv-val"
                type="number"
                value={recordValue}
                onChange={e => setRecordValue(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rv-notes">Notes (optional)</Label>
              <Textarea
                id="rv-notes"
                rows={2}
                value={recordNotes}
                onChange={e => setRecordNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRecordValueDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordValue} disabled={recordValue === ''}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IndicatorList({
  indicators,
  isMeetingTarget,
  onRecord,
  onDelete,
}: {
  indicators: ProjectIndicator[];
  isMeetingTarget: (ind: ProjectIndicator) => boolean | null;
  /** Absent when the user can't manage projects: the list is read-only. */
  onRecord?: (id: string) => void;
  onDelete?: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {indicators.map(ind => {
        const meetingTarget = isMeetingTarget(ind);
        const valueTone =
          meetingTarget === true ? 'text-emerald-500'
          : meetingTarget === false ? 'text-rose-500'
          : '';
        return (
          <Card key={ind.id}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="font-medium">{ind.title}</p>
                  {ind.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{ind.description}</p>
                  )}
                  <div className="mt-1 flex items-center gap-2">
                    <span className={cn('text-sm font-semibold', valueTone)}>
                      {ind.currentValue != null ? ind.currentValue : '-'}
                      {ind.unit ? ` ${ind.unit}` : ''}
                    </span>
                    {ind.targetValue != null && (
                      <span className="text-xs text-muted-foreground">
                        / {ind.targetValue}{ind.unit ? ` ${ind.unit}` : ''}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {ind.direction === 'HigherIsBetter' ? '↑' : '↓'}
                    </span>
                  </div>
                </div>
                {(onRecord || onDelete) && (
                  <div className="flex items-center gap-1">
                    {onRecord && (
                      <Button size="sm" variant="outline" onClick={() => onRecord(ind.id)}>
                        Record
                      </Button>
                    )}
                    {onDelete && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(ind.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
